'use client';

import { hasObjectKey } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, debounce } from 'es-toolkit';
import { useMemo, useRef } from 'react';

import { AuthApi, AuthQueryKey, BOOKMARK_TYPE } from '@/api';
import { ROUTES } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import {
  CONTENT_CATEGORY,
  GTM_EVENT_TYPE,
  trackGtmEvent,
} from '@/utils/analytics/gtm';
import { isSameEndpointQuery } from '@/utils/isSameEndpointQuery';

import { useCustomRouter } from './useCustomRouter';

const WISHLIST_COUNT_KEYS = ['wishlistCount', 'wishlistCnt'] as const;

type BookmarkCountCarrier = Partial<
  Record<(typeof WISHLIST_COUNT_KEYS)[number], number>
> & {
  [key: string]: unknown;
};

interface BookmarkParams {
  /**
   * 쿼리키 조회용
   */
  idx?: string;
  contentId: number;
  isBookmarked: boolean;
  bookmarkType: BOOKMARK_TYPE;
}

interface EventDataParams {
  contentId: number;
  contentName?: string;
  policyAgency?: string;
  policyAgencyId?: string;
}

// 북마크 가능한 항목의 인터페이스
interface BookmarkableItem {
  id: number;
  wishlistYN: boolean;
  [key: string]: unknown;
}

// 컨텐츠 배열을 포함하는 응답 데이터 인터페이스
interface ContentsResponse {
  context: {
    contents: BookmarkableItem[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const OPTIMISTIC_QUERY_KEYS = [
  AuthQueryKey['get:/wello/v2/auth/member/neighbor/policy'],
  AuthQueryKey['get:/wello/v2/auth/policy-apply/history'],
  AuthQueryKey['get:/wello/v2/auth/community/wello-news'],
  AuthQueryKey['get:/wello/v2/auth/member/recent/policy'],
  AuthQueryKey['get:/wello/v2/auth/hometown-news'],
  AuthQueryKey['get:/wello/v2/auth/member/interest/policy'],
  AuthQueryKey['get:/wello/v2/auth/policy/policy/{metaPolicyIdIdx}/related'],
  AuthQueryKey['get:/wello/v2/auth/contents/pick/{welloPickId}'],
  AuthQueryKey['get:/wello/v2/auth/policy'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/policy'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/best-contents'],
  AuthQueryKey['get:/wello/v2/auth/wello-comment'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/emergency-room'],
  AuthQueryKey['get:/wello/v2/auth/emergency-room/{emergencyRoomId}'],
  AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policyId}'],
  AuthQueryKey['get:/wello/v2/auth/contents/feedback/{bestContentsId}'],
  AuthQueryKey['get:/wello/v2/auth/wello-comment/my'],
  AuthQueryKey['get:/wello/v2/auth/community/my'],
  AuthQueryKey['get:/wello/v2/auth/hometown-news/feedback/{hometownNewsId}'],
  AuthQueryKey[
    'get:/wello/v2/auth/community/wello-news/feedback/{communityIdIdx}'
  ],
  AuthQueryKey['get:/wello/v2/auth/member/peer-group/policy'],
  AuthQueryKey['get:/wello/v2/auth/contents/best'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/hometown-news'],
  AuthQueryKey['get:/wello/v2/auth/member/wishlist/community/wello-news'],
  AuthQueryKey['get:/wello/v2/auth/home/institution/policy'],
  AuthQueryKey['get:/wello/v2/auth/recommend-policy'],
];

type QueryObject = {
  query: {
    page: number;
  };
};

const isQueryObject = (obj: object): obj is QueryObject => {
  return 'query' in obj && typeof (obj as QueryObject).query === 'object';
};

/** 북마크 카운트를 업데이트하는 함수 (가능한 경우) */
const updateWishlistCount = (
  item: BookmarkCountCarrier,
  isBookmarked: boolean,
): void => {
  WISHLIST_COUNT_KEYS.forEach((key) => {
    const currentCount = item[key];

    if (typeof currentCount === 'number') {
      item[key] = isBookmarked ? currentCount - 1 : currentCount + 1;
    }
  });
};

export const useBookmark = () => {
  const firstDebounceBookmark = useRef<boolean | null>(null);
  const { mutate } = useMutation({
    mutationFn: ({ contentId, isBookmarked, bookmarkType }: BookmarkParams) =>
      AuthApi[
        isBookmarked
          ? 'delete:/wello/v2/auth/member/wishlist'
          : 'post:/wello/v2/auth/member/wishlist'
      ]({
        body: {
          contentId: contentId,
          contentTypeCd: bookmarkType,
        },
      }),
    meta: {
      reset: [AuthQueryKey['get:/wello/v2/auth/member/action-count']()],
    },
    onSettled: () => {
      firstDebounceBookmark.current = null;
    },
  });

  const queryClient = useQueryClient();
  const router = useCustomRouter();

  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const bookmark = useMemo(() => {
    const debouncedMutate = debounce<typeof mutate>((params, options) => {
      //* 첫 요청(실행 할 수 있는 북마크 액션)과 실제 API 요청의 액션이 일치 할 때만 실행
      //* 옵티미스틱 업데이트와 API 디바운싱 요청으로 인한 un sync 방지
      if (
        firstDebounceBookmark.current !== null &&
        firstDebounceBookmark.current === params.isBookmarked
      ) {
        return mutate(params, options);
      }
    }, 500);

    return (params: BookmarkParams & EventDataParams) => {
      if (firstDebounceBookmark.current === null) {
        firstDebounceBookmark.current = params.isBookmarked;
      }

      requestHaptic();

      const {
        contentId,
        isBookmarked,
        idx,
        contentName,
        bookmarkType,
        policyAgency,
      } = params;

      const { isLogin } = useAuthStore.getState();

      if (!isLogin)
        return router.push(
          ROUTES.LOGIN.withSearchParams({
            searchParams: {
              redirect: location.href,
            },
          }),
        );

      const contentCategory = {
        [BOOKMARK_TYPE.POLICY]: CONTENT_CATEGORY.POLICY,
        [BOOKMARK_TYPE.POLICY_INFO]: CONTENT_CATEGORY.POLICY_NEWS,
        [BOOKMARK_TYPE.HOMETOWN_NEWS]: CONTENT_CATEGORY.HOMETOWN_NEWS,
        [BOOKMARK_TYPE.EMERGENCY_ROOM]: CONTENT_CATEGORY.EMERGENCY_ROOM,
        [BOOKMARK_TYPE.COMMUNITY]: CONTENT_CATEGORY.COMMUNITY,
      }[bookmarkType];

      if (!isBookmarked) {
        trackGtmEvent(GTM_EVENT_TYPE.CLICK_BOOKMARK, {
          content_name: contentName,
          content_category: contentCategory,
          content_id: contentId.toString(),
          policy_agency: policyAgency,
          policy_agency_id: policyAgency,
        });
      }

      const bookmarkQueries = queryClient.getQueriesData({
        predicate: (query) =>
          OPTIMISTIC_QUERY_KEYS.some((queryKey) =>
            isSameEndpointQuery(query, queryKey()),
          ),
      });

      bookmarkQueries.forEach(([key, data]) => {
        const isBaseKeyHasId = (() => {
          const [firstKey] = key;

          const isV2ApiQueryKey = typeof firstKey === 'object';

          if (isV2ApiQueryKey) {
            const StringKey = JSON.stringify(firstKey);

            const id = String(contentId);

            if (StringKey.includes(id) || (idx && StringKey.includes(idx)))
              return true;
          }

          return false;
        })();

        if (
          (key.includes(contentId) ||
            isBaseKeyHasId ||
            (idx && key.includes(idx))) &&
          hasObjectKey(data, 'context') &&
          hasObjectKey(data.context, 'wishlistYN')
        ) {
          const newData = cloneDeep(data);

          if (
            hasObjectKey(newData.context, 'wishlistYN') &&
            typeof newData.context.wishlistYN === 'boolean'
          ) {
            newData.context.wishlistYN = !isBookmarked;

            updateWishlistCount(newData.context, isBookmarked);

            queryClient.setQueryData(key, newData);
          }
        }
        //* 일반 쿼리 (not Infinite Query)
        else if (
          data &&
          typeof data === 'object' &&
          hasObjectKey(data, 'context') &&
          data.context &&
          typeof data.context === 'object'
        ) {
          const newData = cloneDeep(data);

          const updateContents = (contents: unknown[]): unknown[] =>
            contents.map((item) => {
              if (
                item &&
                typeof item === 'object' &&
                'id' in item &&
                typeof item.id === 'number' &&
                item.id === contentId &&
                'wishlistYN' in item &&
                typeof item.wishlistYN === 'boolean'
              ) {
                updateWishlistCount(item, isBookmarked);

                return { ...item, wishlistYN: !isBookmarked };
              }

              return item;
            });

          const hasContents = (obj: unknown): obj is ContentsResponse =>
            obj !== null &&
            typeof obj === 'object' &&
            'context' in obj &&
            obj.context !== null &&
            typeof obj.context === 'object' &&
            'contents' in obj.context &&
            Array.isArray(obj.context.contents);

          let hasAnyChanges = false;

          if (hasContents(newData)) {
            // 1. contents 배열 업데이트
            const updatedContents = updateContents(newData.context.contents);
            const hasChanges = updatedContents.some(
              (item, index) => item !== newData.context.contents[index],
            );

            if (hasChanges) {
              newData.context.contents = updatedContents as BookmarkableItem[];
              hasAnyChanges = true;
            }
          }

          // 2. 타임라인 필드들 업데이트 (past_contents, deadline_contents, future_contents)
          const timelineFields = [
            'pastContents',
            'deadlineContents',
            'futureContents',
          ] as const;

          if (typeof newData.context === 'object' && newData.context !== null) {
            const context = newData.context as Record<string, unknown>;

            for (const field of timelineFields) {
              if (
                hasObjectKey(context, field) &&
                Array.isArray(context[field])
              ) {
                const originalContents = context[field] as unknown[];
                const updatedFieldContents = updateContents(originalContents);
                const hasChanges = updatedFieldContents.some(
                  (item, index) => item !== originalContents[index],
                );

                if (hasChanges) {
                  context[field] = updatedFieldContents as BookmarkableItem[];
                  hasAnyChanges = true;
                }
              }
            }
          }

          if (hasAnyChanges) {
            // 맞춤정책(/policy) scrap_count 업데이트 (정책 북마크인 경우)
            if (
              bookmarkType === BOOKMARK_TYPE.POLICY &&
              hasObjectKey(newData.context, 'scrapCount') &&
              typeof newData.context.scrapCount === 'number'
            ) {
              newData.context.scrapCount = isBookmarked
                ? newData.context.scrapCount - 1
                : newData.context.scrapCount + 1;
            }

            queryClient.setQueryData(key, newData);
          }
        } else
          for (const idKey of [
            'communityId',
            'bestContentsId',
            'hometownNewsId',
            'welloCommentId',
            'policyId',
            'id',
          ] as const) {
            const newData = cloneDeep(data);

            const targetObjectList = ((data) => {
              const result: unknown[] = [];

              const findTargetObject = (obj: unknown) => {
                if (obj && typeof obj === 'object') {
                  if (hasObjectKey(obj, idKey) && obj[idKey] === contentId) {
                    switch (idKey) {
                      case 'id': {
                        const hasId =
                          'metaPolicyIdIdx' in obj || 'uuid' in obj;

                        if (hasId) result.push(obj);

                        break;
                      }

                      default:
                        result.push(obj);
                        break;
                    }
                  }

                  for (const key in obj) {
                    if (hasObjectKey(obj, key)) {
                      findTargetObject(obj[key]);
                    }
                  }
                }
              };

              findTargetObject(data);

              return result;
            })(newData);

            const changeBookmarkCount = (obj: unknown) => {
              if (obj && typeof obj === 'object') {
                updateWishlistCount(obj as BookmarkCountCarrier, isBookmarked);

                for (const key in obj) {
                  if (hasObjectKey(obj, key)) {
                    changeBookmarkCount(obj[key]);
                  }
                }
              }
            };

            const changeBookmarkYN = (obj: unknown) => {
              if (obj && typeof obj === 'object') {
                if (
                  hasObjectKey(obj, 'wishlistYN') &&
                  typeof obj.wishlistYN === 'boolean'
                ) {
                  obj['wishlistYN'] = !isBookmarked;
                }
                for (const key in obj) {
                  if (hasObjectKey(obj, key)) {
                    changeBookmarkYN(obj[key]);
                  }
                }
              }
            };

            targetObjectList.forEach((obj) => {
              changeBookmarkCount(obj);
              changeBookmarkYN(obj);

              const [firstKey] = key;

              const isV2InfiniteQuery =
                firstKey && isQueryObject(firstKey) && '_infinite';

              if (isV2InfiniteQuery) {
                firstKey.query.page = 1;
              }

              queryClient.setQueryData(key, newData);
            });
          }
      });

      debouncedMutate(params, {
        onError: () => {
          //* 쿼리 오류 시 북마크 관련 쿼리 무효화 (옵티미스틱 업데이트 취소)
          bookmarkQueries.forEach(([key]) => {
            queryClient.invalidateQueries({
              queryKey: key,
            });
          });
        },
      });
    };
  }, [mutate, queryClient, requestHaptic, router]);

  return { bookmark };
};
